import { supabase } from './supabase';
import { logger, LogCategory } from './logger';
import { v4 as uuidv4 } from 'uuid';
import { JOB_TYPE, JOB_STATUS, IJob } from '@/types/job';

/**
 * Service for managing background job operations
 * This service provides functionality for creating and tracking jobs
 * that represent backend processes like fission functions
 */
export class JobService {
  private readonly TABLE_NAME = 'jobs';

  /**
   * Create a new job entry
   * @param type The type of job (from JOB_TYPE enum)
   * @param createdBy The ID of the user creating the job
   * @param forecastId Optional ID of the associated forecast
   * @returns The created job or null if there was an error
   */
  async createJob(
    type: JOB_TYPE,
    forecastId?: string
  ): Promise<IJob | null> {
    try {
      logger.info('Creating job', LogCategory.DATABASE, { type, forecastId });
      
      const job: IJob = {
        id: uuidv4(),
        type,
        status: JOB_STATUS.CREATED,
        forecast_id: forecastId
      };
      
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .insert(job)
        .select()
        .single();
      
      if (error) {
        logger.error('Error creating job', LogCategory.DATABASE, { error, job });
        return null;
      }
      
      logger.info('Job created successfully', LogCategory.DATABASE, { jobId: data.id });
      return data;
    } catch (error) {
      logger.error('Exception in createJob', LogCategory.DATABASE, { error, type });
      return null;
    }
  }

  /**
   * Update a job's status
   * @param jobId The ID of the job to update
   * @param status The new status (from JOB_STATUS enum)
   * @returns The updated job or null if there was an error
   */
  async updateJobStatus(jobId: string, status: JOB_STATUS): Promise<IJob | null> {
    try {
      logger.info('Updating job status', LogCategory.DATABASE, { jobId, status });
      
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .update({ 
          status, 
          updated_at: new Date().toISOString() 
        })
        .eq('id', jobId)
        .select()
        .single();
      
      if (error) {
        logger.error('Error updating job status', LogCategory.DATABASE, { error, jobId, status });
        return null;
      }
      
      logger.info('Job status updated successfully', LogCategory.DATABASE, { jobId, status });
      return data;
    } catch (error) {
      logger.error('Exception in updateJobStatus', LogCategory.DATABASE, { error, jobId, status });
      return null;
    }
  }

  /**
   * Get a job by its ID
   * @param jobId The ID of the job to retrieve
   * @returns The job or null if not found
   */
  async getJobById(jobId: string): Promise<IJob | null> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .eq('id', jobId)
        .single();
      
      if (error) {
        logger.error('Error fetching job by ID', LogCategory.DATABASE, { error, jobId });
        return null;
      }
      
      return data;
    } catch (error) {
      logger.error('Exception in getJobById', LogCategory.DATABASE, { error, jobId });
      return null;
    }
  }

  /**
   * Get jobs by forecast ID
   * @param forecastId The forecast ID
   * @returns Array of jobs
   */
  async getJobsByForecastId(forecastId: string): Promise<IJob[]> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .eq('forecast_id', forecastId)
        .order('created_at', { ascending: false });
      
      if (error) {
        logger.error('Error fetching jobs by forecast ID', LogCategory.DATABASE, { error, forecastId });
        return [];
      }
      
      return data || [];
    } catch (error) {
      logger.error('Exception in getJobsByForecastId', LogCategory.DATABASE, { error, forecastId });
      return [];
    }
  }

  /**
   * Get jobs by type
   * @param type The job type (from JOB_TYPE enum)
   * @returns Array of jobs
   */
  async getJobsByType(type: JOB_TYPE): Promise<IJob[]> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .eq('type', type)
        .order('created_at', { ascending: false });
      
      if (error) {
        logger.error('Error fetching jobs by type', LogCategory.DATABASE, { error, type });
        return [];
      }
      
      return data || [];
    } catch (error) {
      logger.error('Exception in getJobsByType', LogCategory.DATABASE, { error, type });
      return [];
    }
  }

  /**
   * Get all jobs
   * @param limit Optional limit on the number of jobs to return
   * @returns Array of jobs
   */
  async getAllJobs(limit?: number): Promise<IJob[]> {
    try {
      let query = supabase
        .from(this.TABLE_NAME)
        .select('*')
        .order('created_at', { ascending: false });
      
      if (limit) {
        query = query.limit(limit);
      }
      
      const { data, error } = await query;
      
      if (error) {
        logger.error('Error fetching all jobs', LogCategory.DATABASE, { error });
        return [];
      }
      
      return data || [];
    } catch (error) {
      logger.error('Exception in getAllJobs', LogCategory.DATABASE, { error });
      return [];
    }
  }

  /**
   * Get jobs by forecast ID and type
   * @param forecastId The forecast ID
   * @param type The job type
   * @returns Array of jobs
   */
  async getJobsByForecastIdAndType(forecastId: string, type: JOB_TYPE): Promise<IJob[]> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .eq('forecast_id', forecastId)
        .eq('type', type)
        .order('created_at', { ascending: false });
      
      if (error) {
        logger.error('Error fetching jobs by forecast ID and type', LogCategory.DATABASE, { error, forecastId, type });
        return [];
      }
      
      return data || [];
    } catch (error) {
      logger.error('Exception in getJobsByForecastIdAndType', LogCategory.DATABASE, { error, forecastId, type });
      return [];
    }
  }

  /**
   * Get the latest job of a specific type for a forecast
   * @param forecastId The forecast ID
   * @param type The job type
   * @returns The latest job or null if none found
   */
  async getLatestJobByForecastIdAndType(forecastId: string, type: JOB_TYPE): Promise<IJob | null> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('*')
        .eq('forecast_id', forecastId)
        .eq('type', type)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      
      if (error) {
        logger.error('Error fetching latest job', LogCategory.DATABASE, { error, forecastId, type });
        return null;
      }
      
      return data;
    } catch (error) {
      logger.error('Exception in getLatestJobByForecastIdAndType', LogCategory.DATABASE, { error, forecastId, type });
      return null;
    }
  }

  /**
   * Check if any job of a specific type has failed for a forecast
   * @param forecastId The forecast ID
   * @param type The job type
   * @returns True if any job has failed, false otherwise
   */
  async hasAnyJobFailed(forecastId: string, type: JOB_TYPE): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('id')
        .eq('forecast_id', forecastId)
        .eq('type', type)
        .eq('status', JOB_STATUS.FAILED)
        .limit(1);
      
      if (error) {
        logger.error('Error checking for failed jobs', LogCategory.DATABASE, { error, forecastId, type });
        return false;
      }
      
      return (data && data.length > 0);
    } catch (error) {
      logger.error('Exception in hasAnyJobFailed', LogCategory.DATABASE, { error, forecastId, type });
      return false;
    }
  }

  /**
   * Check if the latest job of a specific type has failed for a forecast
   * @param forecastId The forecast ID
   * @param type The job type
   * @returns True if the latest job has failed, false otherwise
   */
  async hasLatestJobFailed(forecastId: string, type: JOB_TYPE): Promise<boolean> {
    try {
      const latestJob = await this.getLatestJobByForecastIdAndType(forecastId, type);
      return latestJob !== null && latestJob.status === JOB_STATUS.FAILED;
    } catch (error) {
      logger.error('Exception in hasLatestJobFailed', LogCategory.DATABASE, { error, forecastId, type });
      return false;
    }
  }

  /**
   * Check if a job is currently running for a forecast and type
   * @param forecastId The forecast ID
   * @param type The job type
   * @returns True if a job is running, false otherwise
   */
  async isJobRunning(forecastId: string, type: JOB_TYPE): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE_NAME)
        .select('id')
        .eq('forecast_id', forecastId)
        .eq('type', type)
        .in('status', [JOB_STATUS.CREATED, JOB_STATUS.RUNNING])
        .limit(1);
      
      if (error) {
        logger.error('Error checking for running jobs', LogCategory.DATABASE, { error, forecastId, type });
        return false;
      }
      
      return (data && data.length > 0);
    } catch (error) {
      logger.error('Exception in isJobRunning', LogCategory.DATABASE, { error, forecastId, type });
      return false;
    }
  }
}

// Export a singleton instance
export const jobService = new JobService();